import ReactGA from 'react-ga';
import amplitude from 'amplitude-js';
import { Role } from 'API';
import { GA_ID } from 'env-config';
import { ROUTES } from 'shared/routes';
import { slugToTitleCase } from 'shared/utilities';

interface EventConfig {
  category: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  link_destination?: string;
}
export const AMPLITUDE_ID = '441be1978d1a86f28678763f222028be'; // DEV project

class AnalyticsService {
  getPath = () => window.location.pathname + window.location.search;

  getUrl = () => window.location.origin + window.location.pathname + window.location.search;

  getTitle = (path: string): string => {
    if (path === '/') {
      return 'Home page';
    }

    // If there's a slug, return "Detail: Slug"
    // If no slug, it's a landing page. Return "Landing"
    const format = (
      detail: string, landing: string, slug: string,
    ): string => `${slug ? `${detail}: ${slugToTitleCase(slug)}` : landing}`;

    const root = path.split('/')[1];
    const slug = (path.split('/')[2] || '').split('?')[0];
    switch (`/${root}`) {
      case ROUTES.admin:
        return format('Settings', 'Settings', slug);

      case ROUTES.cases:
        return format('Case', 'Cases', slug);

      case ROUTES.companies:
        return format('Company', 'Contacts', slug);

      case ROUTES.contacts:
      case ROUTES.people:
      case ROUTES.contactsLanding:
        return format('Person', 'Contacts', slug);

      case ROUTES.notifications:
        return 'Notifications';

      case ROUTES.processRequests:
        return format('Process request', 'Process requests', slug);

      case ROUTES.resources:
        return 'Resources';

      default:
        return 'No title';
    }
  };

  initialize = () => {
    // Google Analytics
    ReactGA.initialize(GA_ID, {
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
    });
  };

  logAuthStatechange = (newState: string) => {
    this.logEvent({
      category: 'auth',
      action: 'change_state',
      label: newState,
      nonInteraction: true,
    });
  };

  logEvent = (config: EventConfig) => {
    const {
      category, action, label, link_destination, value, nonInteraction,
    } = config;

    /**
     * Log in Google Analytics
     */
    const eventOptions = {
      ...(nonInteraction && { nonInteraction }),
      ...(link_destination && { dimension1: link_destination }),
    };

    ReactGA.ga('set', 'page', this.getPath());
    ReactGA.ga('send', 'event', category, action, label, value, eventOptions);

    /**
     * Log in Amplitude
     */

    amplitude.getInstance().logEvent(`${category} ${action}`, {
      ...(label && { label }),
      ...(link_destination && { link_destination }),
      ...(value && { value }),
      page: `${window.location.pathname}${window.location.search}`,
    });
  };

  logExternalClick = (text: string, link: string) => {
    this.logEvent({
      category: 'external_link',
      action: 'click',
      label: text,
      link_destination: link,
    });
  };

  logPageview = (pathOverride?: string) => {
    const path = pathOverride || this.getPath();
    ReactGA.pageview(
      path,
      undefined,
      this.getTitle(path),
    );
    amplitude.getInstance().logEvent('pageview', {
      page: path,
      title: this.getTitle(path),
    });
  };

  registerUser = async (person: Person, role: Role) => {
    ReactGA.set({ dimension2: role });

    amplitude.getInstance().setUserId(person.username);
    amplitude.getInstance().setUserProperties({ role });
  };
}

export default new AnalyticsService();
